import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

/**
 * Characters required before the corpus is fetched at all.
 *
 * Below this a prefix barely narrows anything — one or two characters still
 * match most of a large org's corpus, so the request costs the same as the
 * unfiltered one and returns the same truncated head. Waiting also keeps the
 * dropdown from flashing thousands of entries on the first keystroke.
 */
export const MIN_TOPIC_PREFIX_LENGTH = 3;

/**
 * How long typing must pause before the typed text is sent as a filtered
 * request. Shared by every topic autocomplete that filters on the server — the
 * org message-path lookup and the skill editor's per-topic field drill-in — so
 * both wait the same time.
 */
export const TOPIC_PREFIX_DEBOUNCE_MS = 250;

/**
 * Every distinct `topic_name.message_path` string in the org.
 *
 * The slow half of the org topic catalog, kept as its own query so its
 * loading and error states are its own: `/v1/topics/unique/topic_message_paths`
 * 504s for the largest orgs (ENG-2818). Paired with `useOrgTopicNames` by
 * callers that want both — RoboQL completion — so names, which answer in
 * milliseconds, are usable while this one is still in flight.
 *
 * This fetch is unscoped, so its result is the server's cap applied to the
 * alphabet — fine as the instant, approximate list shown before the user has
 * typed enough to narrow anything. Prefix-scoped lookups go through
 * `RoboqlExternalCtx.topics.fetchMessagePaths`, which needs the typed text and
 * therefore cannot be a hook.
 */
export default function useOrgMessagePaths(
  orgId?: string,
  opts?: { enabled?: boolean },
) {
  const { currentOrganization } = useAuth();
  const canonicalOrgId = orgId || currentOrganization?.org_id;
  const { topics: topicService } = useDomainServices();

  return useQuery<string[], Error>({
    queryKey: ["orgMessagePaths", canonicalOrgId],
    queryFn: ({ signal }) => {
      if (canonicalOrgId === undefined) {
        return Promise.reject(new Error("Invalid orgId"));
      }

      return topicService.getMessagePathsForOrg({
        abortSignal: signal,
        resourceOwnerId: canonicalOrgId,
      });
    },
    enabled: canonicalOrgId !== undefined && (opts?.enabled ?? true),
    staleTime: 30000,
  });
}
